import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ComponentData, ComponentRenderMode } from '../../../types/component-skin';
import { QuickMenuItem, QuickMenuLogicReturn } from './QuickMenu.types';
import { fetchProductCategories, selectProductCategories, selectProductCategoriesLoading } from '../../../redux/WithcookieSlice';

/**
 * 퀵 메뉴 비즈니스 로직 훅
 */
const useQuickMenuLogic = (
  componentData: ComponentData,
  mode: ComponentRenderMode
): QuickMenuLogicReturn => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { componentProps = {} } = componentData;
  
  // Redux에서 카테고리 목록 가져오기
  const productCategories = useSelector(selectProductCategories) || [];
  const isLoadingCategories = useSelector(selectProductCategoriesLoading);
  
  // 상태 관리 - 현재 사용하지 않지만 향후 확장을 위해 유지
  // const [loading, setLoading] = useState<boolean>(false);
  const [error] = useState<string | null>(null);
  
  // props에서 categoryItems와 showAllCategory 가져오기
  const { categoryItems = [], showAllCategory = true } = componentProps;
  
  // 컴포넌트 마운트 시 카테고리 목록 로드
  useEffect(() => {
    dispatch(fetchProductCategories() as any);
  }, [dispatch]);
  
  // 최상위 카테고리만 필터링 (parentId가 null인 것들)
  const topCategories = productCategories.filter((category: any) => !category.parentId);
  
  // 표시할 아이템 목록 생성
  // categoryItems에 있는 카테고리 ID와 매칭되는 최상위 카테고리만 표시
  let displayItems: QuickMenuItem[] = categoryItems
    .map((item: QuickMenuItem) => {
      // 해당 카테고리 ID로 최상위 카테고리 찾기
      const category = topCategories.find((cat: any) => cat.id === item.categoryId);
      
      if (!category) {
        return null;
      }
      
      // 카테고리 이름을 URL 친화적으로 변환
      const urlFriendlyName = category.name
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^가-힣A-Za-z0-9-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      
      return {
        ...item,
        categoryName: category.name,
        routingPath: `/${urlFriendlyName}`,
        visible: item.visible !== false // 기본값 true
      };
    })
    .filter((item: QuickMenuItem | null): item is QuickMenuItem => item !== null && item.visible !== false);

  // showAllCategory가 true인 경우 맨 앞에 '전체' 카테고리 추가
  if (showAllCategory) {
    const allCategoryItem: QuickMenuItem = {
      id: 'all-category',
      categoryId: -1, // 전체 카테고리는 특별한 ID 사용
      categoryName: '전체',
      imageUrl: undefined, // 이미지는 컴포넌트에서 처리
      routingPath: '/all',
      visible: true
    };
    displayItems = [allCategoryItem, ...displayItems];
  }
  
  // 아이템 클릭 핸들러
  const handleItemClick = useCallback((item: QuickMenuItem) => {
    // URL 기반으로 프리뷰/에디터 모드 확인
    const isInEditor = window.location.pathname.includes('/builder') || 
                      window.location.pathname.includes('/editor') ||
                      window.location.pathname.includes('/admin');
    const isInPreview = !isInEditor && (
                        window.location.pathname.includes('/preview') || 
                        window.location.pathname.includes('/share') ||
                        window.location.search.includes('preview=true') ||
                        window.location.hostname === 'dev-local.withcookie.com'
                      );
    
    if (mode === 'editor' && !isInPreview) {
      // 에디터 모드이고 프리뷰가 아닌 경우에만 클릭 무시
      return;
    }
    
    // 라우팅 경로가 비어있으면 경고
    if (!item.routingPath) {
      console.warn('⚠️ 라우팅 경로가 설정되지 않았습니다');
      return;
    }
    
    // 라우팅 실행
    const fullPath = `/shopping${item.routingPath}`;
    navigate(fullPath, {
      state: {
        categoryId: item.categoryId,
        categoryName: item.categoryName,
        fromQuickMenu: true
      }
    });
  }, [mode, navigate]);
  
  return {
    data: {
      topCategories,
      displayItems,
      loading: isLoadingCategories,
      error
    },
    actions: {
      handleItemClick
    }
  };
};

export default useQuickMenuLogic;